import { Input } from '@/components/ui/input';
import { RISK_LEVELS, ratingError, riskLevels, withRiskLevel, type RiskFactor } from '@/lib/owaspRating';
import SeverityBadge from './SeverityBadge';

interface RiskRatingFieldProps {
  value: string;
  onChange: (vector: string) => void;
  threatType?: string | null;
}

const FACTORS: { factor: RiskFactor; label: string }[] = [
  { factor: 'likelihood', label: 'Likelihood' },
  { factor: 'impact', label: 'Impact' },
];

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * OWASP Risk Rating editor: quick Likelihood/Impact selectors that write an
 * `OWASP:SIMPLE` vector, plus the raw vector input so `OWASP:ADVANCED`
 * ratings synced from ThreatCat can be viewed and preserved.
 */
export default function RiskRatingField({ value, onChange, threatType }: RiskRatingFieldProps) {
  const levels = riskLevels(value);
  const error = ratingError(value);
  const isAdvanced = !error && value.trim().startsWith('OWASP:ADVANCED/');

  const select = (factor: RiskFactor, level: (typeof RISK_LEVELS)[number]) => {
    const next = withRiskLevel(value, factor, level);
    if (next !== value.trim()) onChange(next);
  };

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground">OWASP Risk Rating</label>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
        {FACTORS.map(({ factor, label }) => (
          <div key={factor} className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-16">{label}</span>
            <div className="inline-flex rounded-md border" role="group" aria-label={label}>
              {RISK_LEVELS.map(level => {
                const pressed = levels?.[factor] === level;
                return (
                  <button
                    key={level}
                    type="button"
                    aria-label={`${label} ${capitalize(level)}`}
                    aria-pressed={pressed}
                    onClick={() => select(factor, level)}
                    className={`px-2.5 py-1 text-xs first:rounded-l-md last:rounded-r-md transition-colors ${
                      pressed ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'
                    }`}
                  >
                    {capitalize(level)}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
        <SeverityBadge vector={error ? null : value} threatType={threatType} showUnrated />
      </div>
      <Input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="OWASP:SIMPLE/L:4/I:4"
        className="font-mono text-xs"
        aria-invalid={!!error}
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {isAdvanced && (
        <p className="text-xs text-muted-foreground">
          Advanced rating — Likelihood and Impact are derived from its factors. Picking a different level replaces it with a Simple rating.
        </p>
      )}
    </div>
  );
}
